import { GiftRoleLabels } from './GiftContactRoles.js';
import { SaveFeedback, SaveConfirmation } from '../../ui/action-feedback.js';
import { useFormPageViewport } from '../../ui/use-form-page-viewport.js';
import {
  SESSION_STATUS_LABELS,
  Client as ClientSchema,
  CrmRevisionConflictEnvelope,
  UpdateClientRequest as UpdateClientSchema,
  type Client,
  type CreateClientRequest,
  type SessionSummary,
  type UpdateClientRequest,
  type DeleteClientRequest,
} from '@diana/contracts';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  ArrowLeft,
  MoreHorizontal,
  Pencil,
  Trash2,
  ChevronRight,
  CalendarDays,
  CircleCheck,
  CircleX,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useEditorCommandRegistration } from '../../app/EditorCommandContext.js';
import { AdminApiError } from '../../lib/api-client.js';
import { readRevisionConflict } from '../../lib/use-conflict.js';
import { useAutosave } from '../../lib/use-autosave.js';
import { Badge, Button, InlineAlert, useDianaUIPortal } from '../../ui/index.js';
import { TextAreaField, TextField } from '../settings/SettingsEditor.js';
import type { AdminApiClient } from '../../lib/api-client.js';
import { ClientAvatar } from './ClientAvatar.js';
import { ClientAvatarEditor } from './ClientAvatarEditor.js';
import { clientContacts, internationalPhone } from './client-contacts.js';
import {
  ClientConflictDialog,
  clientDifferences,
  type ClientComparison,
  type ClientDraft,
} from './ClientConflictDialog.js';
import { ClientDetailsPanel } from './ClientDetailsPanel.js';
import { statusTone } from './crm-format.js';
import { UnsavedChangesGuard } from './UnsavedChangesGuard.js';
import { ClientContactRows } from './ClientContactRows.js';
import { DeleteClientDialog } from './DeleteClientDialog.js';
import { InlineShootNotes } from './InlineShootNotes.js';
import { clientSummary } from './client-summary.js';
import { formatCrmMoney, formatCrmPriceLabel } from './crm-money.js';
import { formatHistoryDate } from './ShootHistory.js';
import './ClientEditor.css';
import './crm-detail-sections.css';

type ClientEditorProps = {
  client: Client;
  shoots: readonly SessionSummary[];
  save(request: UpdateClientRequest): Promise<Client>;
  avatarApi?: AdminApiClient;
  remove?(request: DeleteClientRequest): Promise<void>;
};

export function ClientEditor(props: ClientEditorProps) {
  return <ClientAggregate key={props.client.id} {...props} />;
}

function ClientAggregate({ client, shoots, save, remove, avatarApi }: ClientEditorProps) {
  // Own-save cache updates arrive before the route's save promise resolves. The
  // mounted aggregate owns its revision; only changing client identity resets it.
  const [initialClient] = useState(client);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [notesEditing, setNotesEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const portal = useDianaUIPortal();
  const detailsTrigger = useRef<HTMLButtonElement>(null);
  const submitted = useRef<UpdateClientRequest | null>(null);
  const conflictMetadata = useRef<
    (ClientComparison & { clientId: string; revision: number }) | null
  >(null);
  const [detailsFocus, setDetailsFocus] = useState('Name');
  const autosave = useAutosave<ClientDraft>({
    mode: 'manual',
    initialValue: clientDraft(initialClient),
    initialRevision: initialClient.revision,
    save: async ({ value, expectedRevision }) => {
      const request = { ...value, expectedRevision };
      submitted.current = request;
      conflictMetadata.current = null;
      const result = await save(request);
      return { value: clientDraft(result), revision: result.revision };
    },
    readConflict: (error) => {
      if (!(error instanceof AdminApiError) || error.code !== 'REVISION_CONFLICT') return null;
      const parsed = CrmRevisionConflictEnvelope.safeParse(error.payload);
      if (!parsed.success) return null;
      const envelope = parsed.data.error;
      const current = ClientSchema.safeParse(envelope.current);
      const edit = UpdateClientSchema.safeParse(envelope.submitted);
      const captured = UpdateClientSchema.safeParse(submitted.current);
      if (
        !current.success ||
        !edit.success ||
        current.data.id !== initialClient.id ||
        current.data.revision !== envelope.currentRevision ||
        envelope.expectedRevision !== edit.data.expectedRevision ||
        envelope.currentRevision <= envelope.expectedRevision ||
        !captured.success ||
        edit.data.expectedRevision !== captured.data.expectedRevision ||
        clientDifferences({ current: captured.data, submitted: edit.data }).length > 0
      )
        return null;
      const detected = readRevisionConflict(error, () => clientDraft(current.data));
      if (detected)
        conflictMetadata.current = {
          clientId: initialClient.id,
          revision: detected.serverRevision,
          current: detected.serverValue,
          localSubmitted: submitted.current ?? undefined,
          submitted: {
            name: edit.data.name,
            email: edit.data.email,
            phone: edit.data.phone,
            instagram: edit.data.instagram ?? null,
            notes: edit.data.notes,
            archived: edit.data.archived,
          },
        };
      return detected;
    },
  });
  const { snapshot } = autosave;
  const draft = snapshot.value;
  const conflicted = snapshot.status === 'Conflict';
  const metadata = conflictMetadata.current;
  const comparison =
    conflicted &&
    metadata?.clientId === client.id &&
    metadata.revision === snapshot.conflict?.serverRevision
      ? metadata
      : null;
  const related = shoots
    .filter((shoot) => shoot.clients.some((item) => item.id === client.id))
    .sort((a, b) =>
      a.scheduledStartAt === b.scheduledStartAt
        ? 0
        : !a.scheduledStartAt
          ? 1
          : !b.scheduledStartAt
            ? -1
            : Date.parse(b.scheduledStartAt) - Date.parse(a.scheduledStartAt),
    );
  const summary = clientSummary(related, client.id);
  const contacts = clientContacts(draft);
  const international = internationalPhone(draft.phone);
  const update = (patch: Partial<ClientDraft>) => autosave.edit({ ...draft, ...patch });
  const saveDraft = async (patch?: Partial<ClientDraft>) => {
    if (patch) update(patch);
    if (autosave.getSnapshot().status === 'Error') await autosave.retry();
    else await autosave.flush();
    const result = autosave.getSnapshot();
    return result.status === 'Saved' && !result.dirty;
  };
  const saveError =
    snapshot.error instanceof AdminApiError && snapshot.error.kind === 'validation'
      ? snapshot.error.fields?.map((field) => field.message).join(' ') || snapshot.error.message
      : undefined;
  const changeDetailsOpen = (open: boolean, focus = 'Name') => {
    if (open && notesEditing) {
      if (snapshot.status === 'Saving') return;
      if (snapshot.dirty && !window.confirm('Discard unsaved notes?')) return;
      if (snapshot.dirty) autosave.discardChanges();
      setNotesEditing(false);
    }
    setDetailsFocus(open ? focus : 'Name');
    if (open || conflicted) {
      setDetailsOpen(open);
      return;
    }
    const current = autosave.getSnapshot();
    if (current.status === 'Saving') {
      if (!current.dirty) setDetailsOpen(false);
      return;
    }
    if (current.dirty && !window.confirm('Discard unsaved changes?')) return;
    if (current.dirty) autosave.discardChanges();
    setDetailsOpen(false);
  };

  useEditorCommandRegistration({ status: snapshot.status, save: () => void autosave.flush() });

  return (
    <article className="client-profile">
      <UnsavedChangesGuard
        dirty={snapshot.dirty}
        saving={snapshot.status === 'Saving'}
        discard={autosave.discardChanges}
      />
      <header className="client-profile__header">
        <div className="client-profile__topline">
          <Link className="client-back" to="/crm/clients" aria-label="Back to Clients">
            <ArrowLeft size={20} aria-hidden="true" />
            <span>Back to Clients</span>
          </Link>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              ref={detailsTrigger}
              className="client-button client-button--icon client-profile__more"
              aria-label="More"
            >
              <MoreHorizontal size={20} aria-hidden="true" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal container={portal}>
              <DropdownMenu.Content
                className="dui-menu shoot-menu"
                align="end"
                sideOffset={6}
                onCloseAutoFocus={(event) => {
                  if (detailsOpen || deleteOpen) event.preventDefault();
                }}
              >
                <DropdownMenu.Item
                  className="dui-menu__item"
                  disabled={snapshot.status === 'Saving' || conflicted}
                  onSelect={() => changeDetailsOpen(true)}
                >
                  <Pencil size={16} aria-hidden="true" />
                  Edit client
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="shoot-menu__separator" />
                <DropdownMenu.Item
                  className="dui-menu__item shoot-menu__danger"
                  disabled={!remove || snapshot.status === 'Saving' || conflicted}
                  onSelect={() => {
                    if (snapshot.dirty) {
                      if (!window.confirm('Discard unsaved changes?')) return;
                      autosave.discardChanges();
                    }
                    setNotesEditing(false);
                    setDeleteOpen(true);
                  }}
                >
                  <Trash2 size={16} aria-hidden="true" />
                  Delete client
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          <ClientDetailsPanel
            triggerRef={detailsTrigger}
            open={detailsOpen}
            onOpenChange={changeDetailsOpen}
            focusLabel={detailsFocus}
            conflicted={conflicted}
            title={draft.name}
            status={snapshot.status}
            error={saveError}
            onRetry={() => void autosave.retry()}
            dirty={snapshot.dirty}
            onSave={async () => {
              if (await saveDraft()) {
                setDetailsOpen(false);
                setDetailsFocus('Name');
              }
            }}
          >
            <ClientFields draft={draft} update={update} />
          </ClientDetailsPanel>
        </div>
        <div className="client-profile__header-content">
          <div className="client-profile__identity">
            <>
              {avatarApi ? (
                <ClientAvatarEditor id={client.id} name={draft.name} api={avatarApi} />
              ) : (
                <ClientAvatar
                  id={client.id}
                  name={draft.name}
                  className="client-profile__initials"
                />
              )}
            </>
            <div>
              <h1>{draft.name}</h1>
            </div>
          </div>
          <section className="client-profile__widgets" aria-label="Client summary">
            <div className="client-stat">
              <span>Shoots</span>
              <strong>{summary.shoots}</strong>
              {summary.giftPurchases ? (
                <small className="client-stat__gift-count">
                  {summary.giftPurchases} gift{summary.giftPurchases === 1 ? '' : 's'} bought
                </small>
              ) : null}
            </div>
            <div className="client-stat" title="Unique gallery links saved in this client's shoots">
              <span>Galleries</span>
              <strong>{summary.galleries}</strong>
            </div>
            <div className="client-stat client-stat--money" title="Recorded income minus expenses">
              <span>Earned</span>
              {summary.money === null ? (
                <strong title="Financial records are unavailable for some shoots">—</strong>
              ) : summary.money.length ? (
                summary.money.map(([currency, net]) => (
                  <strong key={currency} data-negative={net < 0 || undefined}>
                    {formatCrmMoney(net, currency)}
                  </strong>
                ))
              ) : (
                <strong>{formatCrmMoney(0, 'EUR')}</strong>
              )}
            </div>
          </section>
        </div>
      </header>
      {!detailsOpen ? (
        <SaveFeedback
          status={snapshot.status}
          error={saveError}
          onRetry={() => void autosave.retry()}
        />
      ) : null}
      <SaveConfirmation
        status={snapshot.status}
        revision={snapshot.revision}
        dirty={snapshot.dirty}
      />
      <div className="client-profile__layout crm-detail-layout">
        <div className="client-profile__main">
          <section
            className="client-profile__shoots crm-detail-section"
            aria-labelledby="client-shoots-title"
          >
            <div className="client-profile__section-heading">
              <h2 id="client-shoots-title" className="crm-section-title">
                Shoots
              </h2>
            </div>
            <div className="client-profile__timeline">
              {related.map((shoot) => (
                <Link
                  key={shoot.id}
                  to={`/crm/shoots/${shoot.id}`}
                  state={{ fromClientId: client.id }}
                  className="client-shoot"
                  data-status={shoot.status}
                >
                  <span className="client-shoot__body">
                    <strong>{shoot.title}</strong>
                    {shoot.isGiftCard && shoot.giftContacts ? (
                      <GiftRoleLabels clientId={client.id} roles={shoot.giftContacts} />
                    ) : null}
                    {shoot.status === 'Delivered' || shoot.status === 'Canceled' ? (
                      <span
                        className="client-shoot__terminal"
                        role="img"
                        aria-label={SESSION_STATUS_LABELS[shoot.status]}
                        title={SESSION_STATUS_LABELS[shoot.status]}
                      >
                        {shoot.status === 'Delivered' ? (
                          <CircleCheck size={19} strokeWidth={1.5} aria-hidden="true" />
                        ) : (
                          <CircleX size={19} strokeWidth={1.5} aria-hidden="true" />
                        )}
                      </span>
                    ) : (
                      <Badge tone={statusTone(shoot.status)}>
                        {SESSION_STATUS_LABELS[shoot.status]}
                      </Badge>
                    )}
                    {shoot.requestedPackageTitle || shoot.requestedPackagePriceLabel ? (
                      <span className="client-shoot__package">
                        {[
                          shoot.requestedPackageTitle,
                          formatCrmPriceLabel(shoot.requestedPackagePriceLabel),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    ) : null}
                    {shoot.scheduledStartAt ? (
                      <span className="client-shoot__date">
                        <CalendarDays size={14} aria-hidden="true" />
                        <time dateTime={shoot.scheduledStartAt}>
                          {compactShootDate(shoot.scheduledStartAt)}
                        </time>
                      </span>
                    ) : null}
                  </span>
                  <ChevronRight size={17} aria-hidden="true" />
                </Link>
              ))}
            </div>
            {!related.length ? (
              <p className="client-profile__empty">No shoots linked yet.</p>
            ) : null}
          </section>
          <InlineShootNotes
            id="client-notes"
            className="client-profile__notes-section"
            value={draft.notes ?? ''}
            editing={notesEditing}
            busy={snapshot.status === 'Saving'}
            disabled={conflicted || detailsOpen}
            dirty={snapshot.dirty}
            onEdit={() => setNotesEditing(true)}
            onChange={(notes) => update({ notes: notes || null })}
            onCancel={() => {
              if (autosave.discardChanges()) setNotesEditing(false);
            }}
            onSave={async () => {
              if (await saveDraft()) setNotesEditing(false);
            }}
          />
        </div>
        <aside className="client-profile__rail">
          <section
            className="shoot-client client-profile__contacts crm-detail-section"
            aria-labelledby="client-contacts-title"
          >
            <h2 id="client-contacts-title" className="crm-section-title">
              Contacts
            </h2>
            <ClientContactRows contacts={contacts} international={international} />
            {!contacts.length ? (
              <button
                className="client-button client-button--outline"
                onClick={() => {
                  changeDetailsOpen(true, 'Email');
                }}
              >
                Add contact details
              </button>
            ) : null}
          </section>
          <div className="client-profile__dates">
            <p>
              Client since{' '}
              {new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(
                new Date(client.createdAt),
              )}
            </p>
            <p>Updated {formatHistoryDate(client.updatedAt)}</p>
          </div>
        </aside>
      </div>
      {deleteOpen ? (
        <DeleteClientDialog
          name={draft.name}
          linked={related.length}
          close={() => {
            setDeleteOpen(false);
            queueMicrotask(() => detailsTrigger.current?.focus());
          }}
          confirm={async () => {
            if (!remove) throw new Error('Unavailable');
            await remove({ expectedRevision: snapshot.revision, confirmed: true });
          }}
        />
      ) : null}
      <ClientConflictDialog
        onRestoreFocus={() => detailsTrigger.current?.focus()}
        open={conflicted && !detailsOpen}
        comparison={comparison}
        latest={draft}
        onAcceptServer={autosave.acceptServerVersion}
        onReapplyLocal={() => {
          autosave.reapplyLocal();
          void autosave.flush();
        }}
        onCopyLocal={async () => {
          await navigator.clipboard.writeText(JSON.stringify(autosave.copyLocal(), null, 2));
        }}
      />
    </article>
  );
}

function ClientFields({
  draft,
  update,
}: {
  draft: Omit<CreateClientRequest, 'id'>;
  update(patch: Partial<Omit<CreateClientRequest, 'id'>>): void;
}) {
  return (
    <div className="client-fields">
      <TextField label="Name" value={draft.name} onChange={(name) => update({ name })} />
      <TextField
        label="Email"
        type="email"
        value={draft.email ?? ''}
        optional
        onChange={(email) => update({ email: email || null })}
      />
      <TextField
        label="Phone"
        value={draft.phone ?? ''}
        optional
        onChange={(phone) => update({ phone: phone || null })}
      />
      <TextField
        label="Instagram"
        value={draft.instagram ?? ''}
        optional
        onChange={(instagram) => update({ instagram: instagram || null })}
      />
      <TextAreaField
        label="Notes"
        value={draft.notes ?? ''}
        optional
        onChange={(notes) => update({ notes: notes || null })}
      />
    </div>
  );
}

export function NewClientEditor({
  create,
}: {
  create(request: CreateClientRequest): Promise<Client>;
}) {
  const { pageRef } = useFormPageViewport();
  const [draft, setDraft] = useState<Omit<CreateClientRequest, 'id'>>({
    name: '',
    email: null,
    phone: null,
    instagram: null,
    notes: null,
  });
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const [error, setError] = useState('');
  return (
    <article ref={pageRef} className="client-create" data-form-page>
      <Link className="client-back" to="/crm/clients">
        <ArrowLeft size={16} aria-hidden="true" />
        Back to Clients
      </Link>
      <header className="client-create__heading">
        <h1>New client</h1>
        <p>Add their contact details and notes.</p>
      </header>
      <section className="client-create__fields" aria-label="Client details">
        <ClientFields draft={draft} update={(patch) => setDraft({ ...draft, ...patch })} />
        {error ? (
          <InlineAlert tone="danger" title="Could not create client">
            {error}
          </InlineAlert>
        ) : null}
      </section>
      <footer className="client-create__footer" data-form-footer>
        <Button
          variant="primary"
          loading={saving}
          disabled={!draft.name.trim()}
          onClick={() => {
            if (busy.current || !draft.name.trim()) return;
            busy.current = true;
            setSaving(true);
            setError('');
            void create({ id: crypto.randomUUID(), ...draft })
              .catch(() => setError('Check the client details and try again.'))
              .finally(() => {
                busy.current = false;
                setSaving(false);
              });
          }}
        >
          Create client
        </Button>
      </footer>
    </article>
  );
}

function clientDraft(client: Client): ClientDraft {
  return {
    name: client.name,
    email: client.email,
    phone: client.phone,
    instagram: client.instagram,
    notes: client.notes,
    archived: Boolean(client.archivedAt),
  };
}

function compactShootDate(value: string) {
  const date = new Date(value);
  const year = new Intl.DateTimeFormat('en-GB', { year: 'numeric', timeZone: 'Europe/Lisbon' });
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    ...(year.format(date) !== year.format(new Date()) ? { year: 'numeric' as const } : {}),
    timeZone: 'Europe/Lisbon',
  }).format(date);
}
